import React, {
  createContext,
  useState,
  useContext,
  useRef,
  useEffect,
  useMemo,
  FC,
} from 'react';
import type { PackageEditorProps } from '@/Pages/Packages/Partials/PackageEditor';
import useTypedPage from '@/Hooks/useTypedPage';
import { router } from '@inertiajs/react';
import useRoute from '@/Hooks/useRoute';
import axios from 'axios';
import { usePlayground } from '@/Contexts/PlaygroundProvider';

export interface FileContentContextProps {
  content: string;
  setContent: Function;
  pasted: boolean;
  setPasted: Function;
  state: FileState;
  setState: Function;
  save: Function;
  synced: boolean;
  reloadContentFromServer: Function;
  isEmpty: boolean;
}

interface FileContentProviderProps {
  children: React.ReactNode;
}

export type FileState = 'idle' | 'changed' | 'saving' | 'saved' | 'failed';

export const FileContentContext = createContext<FileContentContextProps>({
  content: '',
  setContent: () => {},
  pasted: false,
  setPasted: () => {},
  state: 'idle',
  setState: () => {},
  save: () => {},
  synced: true,
  reloadContentFromServer: () => {},
  isEmpty: false,
});

export const useFileContent = () => {
  const context = useContext(FileContentContext);
  if (!context) {
    throw new Error(
      `useFileContent must be used within an FileContentProvider`
    );
  }
  return context;
};

export const FileContentProvider: FC<FileContentProviderProps> = ({
  children,
}) => {
  const { file } = useTypedPage<PackageEditorProps>().props;
  const initialContent = useRef(file.content || '');
  const { getPackageErrors } = usePlayground();
  const [content, setContent] = useState<string>(file.content || '');
  const [pasted, setPasted] = useState<boolean>(false);
  const [state, setState] = useState<FileState>('idle');
  const currentStateRef = useRef(state);
  const route = useRoute();
  const isEmpty = useMemo(() => {
    return content.trim() === '';
  }, [content]);

  const synced = useMemo(() => {
    return state === 'idle' || state === 'saved';
  }, [state]);

  useEffect(() => {
    if (initialContent.current !== content) {
      setState('changed');
    } else {
      setState('idle');
    }
  }, [content]);

  const leaveConfirmation =
    'You have unsaved changes. Are you sure you want to leave?';

  useEffect(() => {
    function handleExit(event: BeforeUnloadEvent) {
      if (currentStateRef.current === 'changed') {
        event.preventDefault();
        (event as BeforeUnloadEvent).returnValue = leaveConfirmation;
        return leaveConfirmation;
      }
    }
    window.addEventListener('beforeunload', handleExit);

    return () => {
      window.removeEventListener('beforeunload', handleExit);
    };
  }, [state]);

  function reloadContentFromServer() {
    if (file) {
      axios.get(route('api.files.content', file.id)).then(response => {
        setContent(response.data);
        setState('idle');
      });
    }
  }

  useEffect(() => {
    currentStateRef.current = state;
  }, [state]);

  useEffect(() => {
    if (content && pasted) {
      save();
    }
  }, [content, pasted]);

  async function save() {
    setState('saving');
    currentStateRef.current = 'saving';
    router.put(
      route('files.update', file.id),
      { content },
      {
        preserveState: true,
        onSuccess: () => {
          setState('saved');
        },
        onError: () => setState('failed'),
        onFinish: () => getPackageErrors(file.package_id),
      }
    );

    setTimeout(() => {
      setState('idle');
    }, 5000);
  }

  return (
    <FileContentContext.Provider
      value={{
        content,
        setContent,
        pasted,
        setPasted,
        state,
        setState,
        save,
        synced,
        reloadContentFromServer,
        isEmpty,
      }}
    >
      {children}
    </FileContentContext.Provider>
  );
};

export default FileContentProvider;
